import React, { FC } from 'react';
import styled from '@emotion/styled';
import Link from 'next/link';
import { mediaQueries, size } from '../../styles/media-queries';
import {
  FontSecondarySemiBold,
  FontPrimaryRegular,
  FontPrimarySemiBold,
  spacingBottom,
  spacingRight,
  spacingTop,
  spacingTopBottom,
} from '@digando/react-component-library';
import { Divider } from 'antd';
import { ContentContainerMax } from '../../layout/container';
import { TenantLogoFragment } from '../../@types/codegen/graphql';
import { useTranslation } from 'next-i18next';
import { TenantLogo } from '../../lib/image/tenant-logo';
import { Hyperlink } from '../elements/Typography/Link/Hyperlink';
import { ZoneTitle } from '../../zones/zone-title';


export interface ISelectedRentalStationSection {
  rentalStationId: string;
  sectionTitle: string;
  WorkingHoursSubtitle: string;
  CompanySubtitle: string;
  partnerName: string;
  address: React.ReactElement;
  distance: string;
  openingHours: string;
  tenantLogo: TenantLogoFragment | undefined;
  tenantPageUrl: string | null;
}

const SelectedRentalStationSectionComponent: FC<ISelectedRentalStationSection> = (props) => {
  const [t] = useTranslation('product-page');
  const {
    sectionTitle,
    CompanySubtitle,
    WorkingHoursSubtitle,
    partnerName,
    address,
    distance,
    openingHours,
    tenantLogo,
    tenantPageUrl
  } = props;

  return (
    <Wrapper>
      <HeadlineInner>
        <ZoneTitle title={sectionTitle} variant={'h3'}/>
      </HeadlineInner>

      <SectionDivider />
      <ContentContainerMax>
        <RentalStationInfos>
          <GeneralInfo>
            <SubTitle>{CompanySubtitle}</SubTitle>

            <TenantLogoContainer>
              {tenantLogo && (
                <TenantLogo logo={tenantLogo} />
              )}
              <TenantName>{partnerName}</TenantName>
            </TenantLogoContainer>

            {tenantPageUrl && (
              <ActionContainer>
                <Link href={tenantPageUrl} passHref={true}>
                  <Hyperlink iconRight="link-arrow">
                    {t('product-page:provider-page')}
                  </Hyperlink>
                </Link>
              </ActionContainer>
            )}
          </GeneralInfo>

          <LocationInfo>
            {address}
            {'' !== distance && (
              <>
                <p>
                  {distance}
                  <span>{t('product-page:selected-rental-station-label')}</span>
                </p>
              </>
            )}
          </LocationInfo>

          <DividerMobileVersionWrapper>
            <SectionDivider />
          </DividerMobileVersionWrapper>

          {openingHours && (
            <OpeningHours>
              <SubTitleOpeningHour>{WorkingHoursSubtitle}</SubTitleOpeningHour>
              <div>{openingHours.replace(',', '\n')}</div>
            </OpeningHours>
          )}
        </RentalStationInfos>
      </ContentContainerMax>
      <SectionDivider />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${spacingBottom(8)}
`;

const HeadlineInner = styled(ContentContainerMax)`
  ${spacingBottom(0)}
`;

const ActionContainer = styled.div`
  background-color: var(--white);
  margin-top: 20px;
  ${spacingTopBottom(2)};
  z-index: 99;
  justify-content: space-between;
  display: flex;
  vertical-align: middle;

  @media (max-width: ${size.tablet}) {
    margin-top: 0;
  }

  @media ${mediaQueries.laptop} {
    ${spacingTopBottom(0)};
    background-color: transparent;
  }

  a {
    align-self: center;

    div {
      height: 100% !important;
      display: flex;
      align-items: center;

      span {
        font-weight: 600;
      }
    }
  }

  .icon-link-arrow {
    display: inline-block;
    transform: rotate(0deg);
    color: var(--primary-color);
    padding-top: 2px;

    @media ${mediaQueries.laptop} {
      padding-top: 3px;
    }
  }
`;

const RentalStationInfos = styled.div`
  ${spacingTop(4)};
  ${spacingBottom(4)};
  grid-column: 1 / 14;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  column-gap: 1vw;
  row-gap: 1vw;

  @media ${mediaQueries.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr auto;
    row-gap: 4vw;
  }

  @media ${mediaQueries.laptopL} {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr;

    .ant-typography {
      ${FontSecondarySemiBold};
      line-height: 29px;
      font-size: var(--font-size-headline-h3);
    }
  }
`;

const OpeningHours = styled.div`
  grid-area: 4 / 1;

  div {
    white-space: pre-line;
    width: 100%;
  }

  @media ${mediaQueries.tablet} {
    grid-area: 2/1;
  }

  @media ${mediaQueries.laptopL} {
    grid-area: 1 / 3;
  }

  .ant-list-item {
    ${spacingTopBottom(0.5)};
    ${FontPrimaryRegular};
    font-size: var(--font-size-text);

    div {
      ${FontPrimaryRegular};
      ${spacingRight(2)};
    }
  }
`;

const GeneralInfo = styled.div`
`;

const TenantLogoContainer = styled.div`
  picture,
  img {
    height: 44px;
    width: auto;
  }
`;

const TenantName = styled.p`
  ${spacingTop(2)};

  ${FontPrimarySemiBold};
  text-align: left;
`;

const LocationInfo = styled.div`
  > p {
    ${spacingBottom(1)};
    max-width: 90vw;

    span {
      margin-left: 4px;
    }

    @media ${mediaQueries.tablet} {
      max-width: 160px;
    }
  }

  @media ${mediaQueries.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    grid-area: 1 / 2;
    margin-top: 38px;

    > p {
      ${spacingBottom(0)};
      max-width: 160px;
    }
  }

  @media ${mediaQueries.laptopL} {
    margin-top: 53px;
  }
`;

const SubTitle = styled.p`
  ${FontSecondarySemiBold};
  font-size: var(--font-size-headline-h4);
  line-height: 24px;
  ${spacingBottom(4)};

  @media ${mediaQueries.tablet} {
    font-size: var(--font-size-headline-h3);
    line-height: 29px;
    display: flex;
    ${spacingBottom(2)};
  }

  @media ${mediaQueries.laptopL} {
    ${spacingBottom(3)};
  }
`;

const SubTitleOpeningHour = styled.p`
  ${FontSecondarySemiBold};
  font-size: var(--font-size-headline-h4);
  line-height: 24px;
  ${spacingBottom(4)};

  @media ${mediaQueries.tablet} {
    font-size: var(--font-size-headline-h3);
    line-height: 29px;
    display: flex;
    ${spacingBottom(2)};
  }

  @media ${mediaQueries.laptopL} {
    ${spacingBottom(3)};
  }
`;

const SectionDivider = styled(Divider)`
  display: none;
  ${spacingTop(0)}
  ${spacingBottom(0)}
  margin: 0;

  @media ${mediaQueries.tablet} {
    display: flex;
    background-color: var(--secondary-color);
    height: 2px;
    opacity: 0.1;
  }
`;

const DividerMobileVersionWrapper = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: var(--secondary-color);
  height: 2px;
  opacity: 0.1;

  @media ${mediaQueries.tablet} {
    display: none;
  }
`;

export const SelectedRentalStationSection = React.memo(SelectedRentalStationSectionComponent, (prevProps, nextProps) => {
  return prevProps.rentalStationId === nextProps.rentalStationId;
});
